import React, { useMemo, useState } from 'react'
import { WarningIcon } from '@chakra-ui/icons'
import { Box, Button, HStack, Placeholders, Row, Table2, Text, useTable } from 'src/ui'
import { useCompanyContext } from 'src/companies/routes/company-context'
import { hasPermission } from 'src/utils/permissions'
import {
  type MappingListItem,
  type MappingStatus,
  useGetTenantCommodityMappings,
} from 'src/api/queries/microservices/platform-markets-commodity-config'
import MappingEditor from './platform-commodity-mapping-editor'

const EMPTY_MAPPINGS: MappingListItem[] = []

function mappingStatusSummary(status: MappingStatus) {
  if (status.type === 'mapped') return `${status.data.name} (${status.data.sourceId})`
  if (status.type === 'perLocationComplete') return 'Mapped per location'
  if (status.type === 'perLocationPartiallyComplete') {
    return (
      <HStack spacing={2}>
        <WarningIcon color="orange.400" />
        <Text>Partially mapped per location</Text>
      </HStack>
    )
  }
  return '—'
}

function PlatformCommodityMappings() {
  const { company } = useCompanyContext()
  const mappingsQuery = useGetTenantCommodityMappings()
  const [selectedCommodity, setSelectedCommodity] = useState<MappingListItem | null>(null)
  const canEdit = hasPermission('company_properties_edit')
  const mappings = mappingsQuery.data ?? EMPTY_MAPPINGS

  const columns = useMemo(
    () => [
      {
        id: 'name',
        accessor: 'commodityName',
        Header: 'Name',
        disableSortBy: true,
      },
      {
        id: 'commodity',
        accessor: 'mappingStatus',
        Header: 'Tenant commodity',
        disableSortBy: true,
        Cell: ({ row }) => mappingStatusSummary(row.original.mappingStatus),
      },
      {
        id: '_actions',
        accessor: '_actions',
        Header: '',
        disableSortBy: true,
        Cell: ({ row }) => (
          <Row justifyContent="flex-end">
            <Button
              size="sm"
              variant="ghost"
              color="primary.500"
              isDisabled={!canEdit}
              onClick={() => setSelectedCommodity(row.original)}
            >
              Edit
            </Button>
          </Row>
        ),
      },
    ],
    [canEdit]
  )

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    data: mappings,
    tableOptions: { initialState: { sortBy: [] } },
    columns,
  })

  if (mappingsQuery.isPending) return <Placeholders.LoadingState mt={8} />
  if (mappingsQuery.isError) return <Placeholders.FailedState mt={8} />

  if (selectedCommodity) {
    return (
      <MappingEditor
        commodity={selectedCommodity}
        tenantId={company.tenant_id}
        canEdit={canEdit}
        onClose={() => setSelectedCommodity(null)}
      />
    )
  }

  if (!mappings.length) {
    return <Placeholders.EmptyState message="No platform commodities found" />
  }

  return (
    <Box bg="white" borderRadius={3} boxShadow="md" overflow="hidden">
      <Table2 {...getTableProps()} striped>
        <Table2.Header headerGroups={headerGroups} />
        <Table2.Body rows={rows} getTableBodyProps={getTableBodyProps} prepareRow={prepareRow} />
      </Table2>
    </Box>
  )
}

export default function PlatformCommodityMappingsIndex() {
  const { company } = useCompanyContext()
  return <PlatformCommodityMappings key={`${company.tenant_id}:${company.datasource_id}`} />
}
